// Copyright (c) Docugami, Inc. All rights reserved.

import { lightHighlightBlue } from "components/shell/presentation/AppStyles";
import { useEffect } from "react";
import { folderIdSelector } from "store/dgml/dgmlSelectors";
import { setLabelIsEditing, useGetBindingsQuery } from "store/reports/reportsSlice";
import { selectLabelChunks, selectReportId, selectSelectedLabelState } from "store/reports/selectors";
import { removePreviewChunkSet, setPreviewChunks } from "store/sdvSlice";
import { useAppDispatch, useAppSelector } from "./redux";

interface ILabelChunks {
    label: string | undefined;
    labelId?: string;
    isEditing: boolean;
    chunks: string[];
}

// Returns a live view of the selected label's chunks
export function useLabelChunks(documentId: string, multiSelectEnabled: boolean): ILabelChunks {
    const selectedLabel = useAppSelector(selectSelectedLabelState);

    // Report-specific state
    const fileId = folderIdSelector(documentId)?.fileId;
    const reportId = useAppSelector(selectReportId);
    useGetBindingsQuery({ reportId, fileId: fileId ?? '' });

    const chunks = useAppSelector(state => selectLabelChunks(state, selectedLabel?.label ?? '', documentId));

    const dispatch = useAppDispatch();

    useEffect(() => {
        dispatch(setLabelIsEditing(!!multiSelectEnabled));
    }, [multiSelectEnabled, dispatch])

    // Set the canvas display chunks for the selected label
    useEffect(() => {
        if (!multiSelectEnabled) {
            dispatch(setPreviewChunks({
                id: 'selected-label',
                chunks: { spanIds: chunks, color: lightHighlightBlue }
            }))
        }
        else {
            dispatch(removePreviewChunkSet('selected-label'));
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps 
        [selectedLabel, multiSelectEnabled])

    return {
        label: selectedLabel?.label,
        labelId: selectedLabel?.id,
        isEditing: !!selectedLabel?.isEditing,
        chunks: chunks
    }
}